import {QueryCacheKeys} from '../../api/cache';
import {getInvoice, saveInvoice} from '../../api/invoices';
import {areInvoicesEqual, isInvoiceValid, isLineItemEmpty} from '../../invoice-helpers';
import Layout from '../Layout';
import InvoiceEditForm from './InvoiceEditForm';
import {Button, LoaderButton} from '@sproutsocial/racine';
import {useMutation, useQuery} from '@tanstack/react-query';
import {DateTime} from 'luxon';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Invoice, InvoiceLineItem, InvoiceLineItemType} from 'shared/src/models';

interface InvoiceEditPageProps {
	invoiceId: string | null;
}

const InvoiceEditPage = ({invoiceId}: InvoiceEditPageProps) => {
	const {data: invoice} = useQuery({
		queryKey: QueryCacheKeys.invoice(invoiceId),
		queryFn: () => getInvoice(invoiceId!),
		enabled: Boolean(invoiceId),
	});

	const {mutateAsync: saveInvoiceAsync, isPending: isSaving} = useMutation({
		mutationFn: saveInvoice,
	});

	const [editedInvoice, setEditedInvoice] = useState<Invoice | null>(() => {
		return invoiceId ? invoice ?? null : emptyInvoice();
	});

	useEffect(() => {
		if (invoiceId && invoice && !editedInvoice) {
			setEditedInvoice(invoice);
		}
	}, [invoiceId, invoice, editedInvoice, setEditedInvoice]);

	const invoiceChanged = useMemo<boolean>(() => {
		if (!invoice) {
			return true;
		}

		// This only happens before the invoice is loaded
		if (!editedInvoice) {
			return false;
		}

		return !areInvoicesEqual(editedInvoice, invoice);
	}, [invoice, editedInvoice]);

	const onInvoiceUpdated = useCallback(
		(updatedInvoice: Invoice) => {
			if (
				updatedInvoice.items.length === 0 ||
				!isLineItemEmpty(updatedInvoice.items[updatedInvoice.items.length - 1])
			) {
				updatedInvoice.items.push(emptyLineItem());
			}

			setEditedInvoice(updatedInvoice);
		},
		[setEditedInvoice],
	);

	const onSave = useCallback(async () => {
		if (!editedInvoice) {
			return;
		}

		const id = await saveInvoiceAsync(editedInvoice);

		window.location.href = `/invoices/${id}/edit`;
	}, [editedInvoice, saveInvoiceAsync]);

	const isSaveEnabled = editedInvoice && invoiceChanged && isInvoiceValid(editedInvoice);

	if (!editedInvoice) {
		return null;
	}

	return (
		<Layout>
			<Layout.Header title={invoiceId ? 'Edit Invoice' : 'Create Invoice'}>
				<form
					method='POST'
					action={`/api/invoices/pdf/${editedInvoice.id}`}
					style={{display: 'inline'}}
				>
					<Button
						appearance='secondary'
						mr='space.300'
						disabled={invoiceChanged}
						// @ts-ignore
						type='submit'
					>
						Download PDF
					</Button>
				</form>
				<Button
					appearance='secondary'
					mr='space.300'
					disabled={invoiceChanged}
					href={`/preview/${editedInvoice.id}`}
					external
				>
					Preview
				</Button>
				<LoaderButton
					appearance='primary'
					width='100px'
					disabled={!isSaveEnabled}
					isLoading={isSaving}
					onClick={onSave}
				>
					Save
				</LoaderButton>
			</Layout.Header>
			<Layout.Body>
				<InvoiceEditForm invoice={editedInvoice} onInvoiceUpdated={onInvoiceUpdated} />
			</Layout.Body>
		</Layout>
	);
};

const emptyInvoice = (): Invoice => ({
	id: null as any,
	publicId: 0,
	date: DateTime.now(),
	description: '',
	customer: '',
	items: [emptyLineItem()],
});

const emptyLineItem = (): InvoiceLineItem => ({
	id: crypto.randomUUID(),
	type: InvoiceLineItemType.Cost,
	name: '',
	description: '',
	quantity: null,
	price: 0,
	taxed: false,
});

export default InvoiceEditPage;
